import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, X, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Aspect-preserving resize → JPEG data URL.
async function fileToDataUrl(file, maxDim = 800) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = reject;
    reader.onload = (e) => {
      const img = new Image();
      img.onerror = reject;
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

const PERSON_CATEGORIES = ["Mentor", "Family", "Friend", "Beta Tester", "Cheerleader", "Other"];
const COMPANY_CATEGORIES = ["Gym", "Equipment", "Podcast", "Brand", "Studio", "Other"];

/**
 * SupporterSubmitDialog — public self-submit form. Anyone can submit; owner reviews.
 * Posts to /api/supporters/submit (rate-limited to 5/IP/24h).
 */
export default function SupporterSubmitDialog({ open, onClose }) {
  const [kind, setKind] = useState("person");
  const [form, setForm] = useState({
    name: "",
    category: "Friend",
    tagline: "",
    note: "",
    image_url: "",
    link: "",
    contact_email: "",
    why: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const [autoPublished, setAutoPublished] = useState(false);
  const fileRef = useRef(null);

  const categories = kind === "company" ? COMPANY_CATEGORIES : PERSON_CATEGORIES;

  async function handleFile(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed.");
      return;
    }
    if (file.size > 8_000_000) {
      toast.error("File too large (max 8 MB before compression).");
      return;
    }
    setUploading(true);
    try {
      const dataUrl = await fileToDataUrl(file, 800);
      setForm((f) => ({ ...f, image_url: dataUrl }));
    } catch {
      toast.error("Couldn't process that image.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function submit() {
    if (!form.name.trim()) {
      toast.error("Please add your name.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/supporters/submit`, {
        ...form,
        kind,
        category: form.category || (kind === "company" ? "Brand" : "Friend"),
      });
      setAutoPublished(!!data?.auto_published);
      setDone(true);
      toast.success(
        data?.auto_published
          ? "Live on the wall — you're a trusted supporter ✨"
          : "Submission received — Roc will review it soon!",
        { duration: 5000 }
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  }

  function reset() {
    setKind("person");
    setForm({
      name: "",
      category: "Friend",
      tagline: "",
      note: "",
      image_url: "",
      link: "",
      contact_email: "",
      why: "",
    });
    setDone(false);
    setAutoPublished(false);
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/85 z-[70] flex items-center justify-center p-4 overflow-y-auto"
      onClick={() => !submitting && onClose()}
    >
      <div
        className="bg-[#0F0F0F] border-2 border-[#D4AF37] max-w-xl w-full p-6 sm:p-8 my-8 relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="supporter-submit-dialog"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 text-neutral-500 hover:text-white"
          data-testid="supporter-submit-close"
        >
          <X size={18} />
        </button>

        {done ? (
          <div className="text-center py-8" data-testid={autoPublished ? "supporter-submit-done-auto" : "supporter-submit-done-pending"}>
            <div className="text-5xl mb-4">{autoPublished ? "✨" : "🐦"}</div>
            <h2 className="font-display text-3xl text-white mb-2">
              {autoPublished ? "You're live!" : "Thank you!"}
            </h2>
            <p className="text-neutral-400 mb-6 max-w-md mx-auto">
              {autoPublished
                ? "As a trusted supporter, your card was auto-published to the OmniMuscle wall instantly."
                : "Your submission is in Roc's inbox. If approved, your card will appear on the OmniMuscle wall."}
            </p>
            <div className="flex gap-3 justify-center flex-wrap">
              {autoPublished && (
                <a
                  href="/supporters"
                  data-testid="supporter-submit-view-wall"
                  className="border border-[#D4AF37] hover:bg-[#D4AF37] hover:text-black text-[#D4AF37] font-bold uppercase tracking-widest text-xs py-2.5 px-4 transition-colors"
                >
                  View on the Wall →
                </a>
              )}
              <button
                type="button"
                onClick={reset}
                data-testid="supporter-submit-another"
                className="border border-zinc-700 hover:border-white text-white font-bold uppercase tracking-widest text-xs py-2.5 px-4 transition-colors"
              >
                Submit Another
              </button>
              <button
                type="button"
                onClick={onClose}
                data-testid="supporter-submit-done"
                className="bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A] font-bold uppercase tracking-[0.2em] text-xs py-2.5 px-5 transition-all"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] font-bold mb-1">
              Join the Wall
            </div>
            <h2 className="font-display text-3xl text-white mb-1">
              Become a Supporter
            </h2>
            <p className="text-xs text-neutral-500 mb-5">
              Tell Roc who you are. If approved, you'll appear on the OmniMuscle Friends &amp; Supporters wall.
            </p>

            {/* Kind */}
            <div className="grid grid-cols-2 gap-2 mb-4" data-testid="submit-kind">
              <KindBtn active={kind === "person"} onClick={() => { setKind("person"); setForm((f) => ({ ...f, category: "Friend" })); }} testId="submit-kind-person">
                🐦 I'm a Person
              </KindBtn>
              <KindBtn active={kind === "company"} onClick={() => { setKind("company"); setForm((f) => ({ ...f, category: "Brand" })); }} testId="submit-kind-company">
                🤝 We're a Company
              </KindBtn>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <Field label={kind === "company" ? "Company Name *" : "Your Name *"}>
                <input
                  data-testid="submit-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder={kind === "company" ? "e.g. Iron Castle Gym" : "e.g. Jane Doe"}
                  className="form-input"
                  maxLength={80}
                />
              </Field>
              <Field label="Role / Category">
                <select
                  data-testid="submit-category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="form-input"
                >
                  {categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
            </div>

            <Field label="Tagline (one-liner)">
              <input
                data-testid="submit-tagline"
                value={form.tagline}
                onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                placeholder={kind === "company" ? "What does your company do?" : "How do you support OmniMuscle?"}
                className="form-input"
                maxLength={280}
              />
            </Field>

            <Field label="Personal Note (optional)">
              <textarea
                data-testid="submit-note"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                placeholder="A quick note Roc can read"
                rows={2}
                className="form-input resize-none"
                maxLength={600}
              />
            </Field>

            {/* Image */}
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1">
                {kind === "company" ? "Logo" : "Photo"} (optional)
              </div>
              <div className="flex gap-3 items-start">
                <div className={
                  "flex-shrink-0 w-20 h-20 bg-black border border-zinc-800 overflow-hidden relative " +
                  (kind === "person" ? "rounded-full" : "")
                }>
                  {form.image_url ? (
                    <>
                      <img src={form.image_url} alt="preview" className="w-full h-full object-contain" />
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, image_url: "" })}
                        title="Clear image"
                        className="absolute top-0 right-0 bg-black/80 text-red-400 hover:text-red-200 p-0.5"
                        data-testid="submit-image-clear"
                      >
                        <X size={12} />
                      </button>
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[10px] uppercase tracking-widest text-neutral-700">
                      No img
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    className="hidden"
                    data-testid="submit-image-file"
                  />
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    data-testid="submit-image-upload-btn"
                    className="w-full px-3 py-2 text-[10px] uppercase tracking-widest font-bold border-2 border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Upload size={12} />
                    {uploading ? "Compressing…" : "Upload Image"}
                  </button>
                  <div className="text-[9px] uppercase tracking-widest text-neutral-600 mt-1">
                    JPG/PNG up to 8 MB
                  </div>
                </div>
              </div>
            </div>

            <Field label="Link (website, IG, podcast — optional)">
              <input
                data-testid="submit-link"
                value={form.link}
                onChange={(e) => setForm({ ...form, link: e.target.value })}
                placeholder="https://…"
                className="form-input"
                maxLength={500}
              />
            </Field>

            <Field label="Contact Email (optional, only Roc sees this)">
              <input
                type="email"
                data-testid="submit-email"
                value={form.contact_email}
                onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                placeholder="you@example.com"
                className="form-input"
                maxLength={120}
              />
            </Field>

            <Field label="Why you'd love to be on the wall (optional)">
              <textarea
                data-testid="submit-why"
                value={form.why}
                onChange={(e) => setForm({ ...form, why: e.target.value })}
                placeholder="A sentence or two for Roc"
                rows={2}
                className="form-input resize-none"
                maxLength={600}
              />
            </Field>

            <div className="flex gap-3 mt-5">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                data-testid="submit-cancel"
                className="flex-1 border border-zinc-700 hover:border-white text-white font-bold uppercase tracking-widest text-xs py-3 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                data-testid="submit-send"
                className="flex-1 bg-[#D4AF37] hover:bg-[#F1C84B] text-[#0A0A0A] font-bold uppercase tracking-[0.2em] text-xs py-3 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting ? "Sending…" : <><Send size={12} /> Submit</>}
              </button>
            </div>
          </>
        )}

        <style>{`
          .form-input {
            width: 100%;
            background: #0A0A0A;
            border: 1px solid #27272a;
            color: #ffffff;
            padding: 0.5rem 0.75rem;
            font-size: 0.875rem;
            outline: none;
            transition: border-color 0.15s;
          }
          .form-input:focus { border-color: #D4AF37; }
        `}</style>
      </div>
    </div>
  );
}

function KindBtn({ active, onClick, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={
        "px-3 py-2.5 text-xs font-bold uppercase tracking-widest border-2 transition-colors " +
        (active
          ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
          : "border-zinc-800 text-neutral-400 hover:border-zinc-600")
      }
    >
      {children}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3">
      <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold mb-1">
        {label}
      </div>
      {children}
    </div>
  );
}
